import { useQuery } from '@tanstack/react-query';
import type { PayablesSummaryDTO, SupplierInvoiceDTO } from '@bifeai/shared';
import { Card, CardBody, CardHeader, EmptyState, Kpi, PageHeader, Skeleton } from '@bifeai/ui';
import { ArrowLeft, CalendarClock, Landmark, ReceiptText, Wallet } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { dueText, invoiceLabel } from './PayDialog';

/** Pagos a proveedores: lo que se debe, lo vencido, lo que vence esta semana y los cheques por cubrir. */
export function PayablesPage() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['payables', 'summary'], queryFn: () => api.get<PayablesSummaryDTO>('/payables') });
  const d = q.data;
  // Una fila por proveedor: lo que se le debe y su factura que vence antes.
  const bySupplier = new Map<string, { name: string; total: number; first: SupplierInvoiceDTO; count: number }>();
  for (const i of d?.invoices ?? []) {
    const cur = bySupplier.get(i.supplierId);
    if (cur) {
      cur.total += i.pendingCents;
      cur.count += 1;
    } else bySupplier.set(i.supplierId, { name: i.supplierName, total: i.pendingCents, first: i, count: 1 });
  }
  const checksTotal = (d?.checks ?? []).reduce((a, c) => a + c.amountCents, 0);

  return (
    <>
      <Link to="/proveedores" className="bf-back">
        <ArrowLeft size={16} /> Proveedores
      </Link>
      <PageHeader title="Pagos a proveedores" subtitle="Lo que se debe, lo que vence y los cheques que tienen que tener fondos." />
      {!d ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <>
          <div className="bf-kpi-grid">
            <Kpi
              label="Se debe en total"
              tone="amber"
              icon={<Wallet />}
              value={formatMoney(d.totalCents)}
              delta={bySupplier.size === 1 ? '1 proveedor' : `${bySupplier.size} proveedores`}
            />
            <Kpi
              label="Vencido"
              tone="red"
              icon={<ReceiptText />}
              value={formatMoney(d.overdueCents)}
              delta={d.overdueCents ? 'Pagalo primero' : 'Nada vencido'}
            />
            <Kpi label="Vence en 7 días" tone="celeste" icon={<CalendarClock />} value={formatMoney(d.dueSoonCents)} />
            <Kpi
              label="Cheques por cubrir"
              tone="green"
              icon={<Landmark />}
              value={formatMoney(checksTotal)}
              delta={d.checks.length === 1 ? '1 cheque' : `${d.checks.length} cheques`}
            />
          </div>

          <Card>
            <CardHeader title="A quién se le debe" subtitle="Tocá un proveedor para pagarle o ver su cuenta." />
            <CardBody flush>
              {bySupplier.size === 0 ? (
                <EmptyState icon={<Wallet />} title="No se le debe nada a nadie" />
              ) : (
                <div className="bf-table-wrap">
                  <table className="bf-table">
                    <thead>
                      <tr>
                        <th>Proveedor</th>
                        <th>Próximo vencimiento</th>
                        <th style={{ textAlign: 'right' }}>Se debe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...bySupplier.entries()].map(([id, s]) => {
                        const due = dueText(s.first);
                        return (
                          <tr key={id} data-clickable="true" onClick={() => navigate(`/proveedores/${id}`)}>
                            <td>
                              <span className="bf-cell-main">{s.name}</span>
                              <div className="bf-cell-sub">{s.count === 1 ? '1 factura' : `${s.count} facturas`}</div>
                            </td>
                            <td className={due.tone === 'danger' ? 'bf-text-danger' : due.tone === 'warning' ? 'bf-text-warning' : 'bf-cell-sub'}>
                              {due.text}
                              <div className="bf-cell-sub">{invoiceLabel(s.first)}</div>
                            </td>
                            <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                              {formatMoney(s.total)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Cheques por cubrir"
              subtitle="Cheques entregados que todavía no cobraron: ese día la plata tiene que estar en el banco."
            />
            <CardBody flush>
              {d.checks.length === 0 ? (
                <EmptyState icon={<Landmark />} title="No hay cheques pendientes" />
              ) : (
                <table className="bf-table">
                  <tbody>
                    {d.checks.map((c) => (
                      <tr key={c.id}>
                        <td className="bf-cell-main">{formatDate(c.checkDueDate)}</td>
                        <td>
                          {c.supplierName}
                          {c.reference && <div className="bf-cell-sub">{c.reference}</div>}
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatMoney(c.amountCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
}
